import React, { useState, useEffect, useMemo, useRef } from 'react';
import { appointmentService, Service, ServiceCategory, Professional } from '../services/appointmentService';
import { formatCurrency } from '../utils';
import { ChevronRight, Clock, User, Calendar, Check, ArrowLeft, Plus, Edit2, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

// Función para decodificar JWT y obtener claims
function decodeJwt(token: string): any {
  try {
    const payload = token.split('.')[1];
    return JSON.parse(atob(payload));
  } catch {
    return null;
  }
}

// Horario para clientes: 11:00 a 19:00
const CLIENT_OPEN = '11:00';
const CLIENT_CLOSE = '19:00';
// Horario para administradores: 08:00 a 21:00
const ADMIN_OPEN = '08:00';
const ADMIN_CLOSE = '21:00';
const SLOT_INTERVAL_MIN = 15;

function timeToMinutes(t: string) {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
}

function minutesToTime(mins: number) {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function generateSlots(open: string, close: string, interval = SLOT_INTERVAL_MIN) {
  const start = timeToMinutes(open);
  const end = timeToMinutes(close);
  const slots: string[] = [];
  for (let t = start; t + interval <= end; t += interval) {
    slots.push(minutesToTime(t));
  }
  return slots;
}

type Step = 'branch' | 'categories' | 'services' | 'professionals' | 'schedule' | 'summary';

interface ServiceGroup {
  id: string;
  services: Service[];
  possibleProfessionals: Professional[];
  selectedProfessionalId: number | null;
  startTime?: string;
  endTime?: string;
}

const SchedulingPage: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const [currentStep, setCurrentStep] = useState<Step>('branch');
  const [selectedBranch, setSelectedBranch] = useState<'antofagasta' | 'santiago' | null>(null);
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [loadingCategories, setLoadingCategories] = useState<boolean>(false);
  const [selectedCategory, setSelectedCategory] = useState<ServiceCategory | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filterCategory, setFilterCategory] = useState<string>('Todos');
  
  // Estado global de servicios seleccionados (acumulativo)
  const [selectedServices, setSelectedServices] = useState<Service[]>([]);
  
  const [allProfessionals, setAllProfessionals] = useState<Professional[]>([]);
  
  // Grupos de servicios para asignación de profesionales
  const [serviceGroups, setServiceGroups] = useState<ServiceGroup[]>([]);

  // Inicializar selectedDate evitando domingos
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    // Si hoy es domingo (0), avanzar al lunes
    if (today.getDay() === 0) {
      today.setDate(today.getDate() + 1);
    }
    return today;
  });
  
  // Estado para la selección individual de horarios
  const [activeGroupId, setActiveGroupId] = useState<string | null>(null);
  const [availableSlotsForGroup, setAvailableSlotsForGroup] = useState<string[]>([]);

  const [isLoading, setIsLoading] = useState(false);
  const [customerName, setCustomerName] = useState<string>('');
  const [customerPhone, setCustomerPhone] = useState<string>('');
  const [customerEmail, setCustomerEmail] = useState<string>('');
  const [confirmationMsg, setConfirmationMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Determinar si el usuario es administrador
  const isAdmin = useMemo(() => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) return false;
      const claims = decodeJwt(token);
      return claims?.is_staff === true;
    } catch {
      return false;
    }
  }, [isAuthenticated]);

  // Horarios dinámicos según rol
  const scheduleOpen = isAdmin ? ADMIN_OPEN : CLIENT_OPEN;
  const scheduleClose = isAdmin ? ADMIN_CLOSE : CLIENT_CLOSE;

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Cargar categorías
  useEffect(() => {
    if (!selectedBranch) return;
    const loadCategories = async () => {
      setLoadingCategories(true);
      try {
        const res = selectedBranch === 'santiago' 
          ? await appointmentService.getAllCategoriesSantiago()
          : await appointmentService.getAllCategories();
        const normalized = (res || []).map((c: ServiceCategory) => ({ 
          ...c, 
          services: Array.isArray((c as any).services) ? (c as any).services : [] 
        }));
        normalized.sort((a: ServiceCategory, b: ServiceCategory) => a.name.localeCompare(b.name));
        setCategories(normalized);
      } catch (err) {
        console.error('Error loading service categories', err);
        setCategories([]);
      } finally {
        setLoadingCategories(false);
      }
    };
    loadCategories();
  }, [selectedBranch]);

  // Cargar profesionales
  useEffect(() => {
    if (!selectedBranch) return;
    const loadProfessionals = async () => {
      try {
        const data = selectedBranch === 'santiago'
          ? await appointmentService.getAllProfessionalsSantiago()
          : await appointmentService.getAllProfessionals();
        setAllProfessionals(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error cargando profesionales', err);
        setAllProfessionals([]);
      }
    };
    loadProfessionals();
  }, [selectedBranch]);

  // Prefill customer data when authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
      setCustomerName((prev) => fullName || prev);
      setCustomerEmail((prev) => user.email || prev);
      setCustomerPhone((prev) => user.phone_number || prev);
    }
  }, [isAuthenticated, user]);

  // Lógica de agrupación de servicios cuando se entra al paso 'professionals'
  useEffect(() => {
    if (currentStep === 'professionals' && selectedServices.length > 0) {
      // Si ya tenemos grupos y los servicios no han cambiado, intentamos mantener la selección
      // Pero para simplificar, recalculamos grupos si cambia la longitud o ids.
      // Aquí implementamos la lógica "Greedy" para agrupar servicios compatibles.
      
      const serviceProfs = selectedServices.map(s => ({
        service: s,
        profs: allProfessionals.filter(p => 
          s.professionals_detail?.some(sp => sp.id === p.id)
        )
      }));

      const newGroups: ServiceGroup[] = [];
      let remaining = [...serviceProfs];

      while (remaining.length > 0) {
        let currentGroupServices = [remaining[0]];
        let currentIntersection = remaining[0].profs;
        
        let i = 1;
        while (i < remaining.length) {
          const candidate = remaining[i];
          const intersection = currentIntersection.filter(p => 
            candidate.profs.some(cp => cp.id === p.id)
          );
          
          if (intersection.length > 0) {
            currentGroupServices.push(candidate);
            currentIntersection = intersection;
            remaining.splice(i, 1);
          } else {
            i++;
          }
        }
        remaining.shift();

        newGroups.push({
          id: Math.random().toString(36).substr(2, 9),
          services: currentGroupServices.map(x => x.service),
          possibleProfessionals: currentIntersection,
          selectedProfessionalId: null
        });
      }
      setServiceGroups(newGroups);
    }
  }, [currentStep, selectedServices, allProfessionals]);

  // Cálculo de slots disponibles para un grupo específico
  useEffect(() => {
    if (currentStep !== 'schedule' || !activeGroupId) return;
    
    const group = serviceGroups.find(g => g.id === activeGroupId);
    if (!group || !group.selectedProfessionalId) return;

    const fetchSlots = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const dateKey = selectedDate.toISOString().split('T')[0];
        
        // 1. Obtener disponibilidad del profesional seleccionado
        const taken = selectedBranch === 'santiago'
          ? await appointmentService.getAvailableSlotsSantiago(dateKey, group.selectedProfessionalId!)
          : await appointmentService.getAvailableSlots(dateKey, group.selectedProfessionalId!);
        
        // 2. Duración del grupo
        const duration = group.services.reduce((sum, s) => sum + s.duration_minutes, 0);
        
        // 3. Generar slots base usando horarios dinámicos según rol
        const allSlots = generateSlots(scheduleOpen, scheduleClose);
        const validSlots: string[] = [];

        // Validar si es hoy para filtrar horas pasadas
        const now = new Date();
        const isToday = selectedDate.getDate() === now.getDate() &&
                        selectedDate.getMonth() === now.getMonth() &&
                        selectedDate.getFullYear() === now.getFullYear();
        const currentMinutes = now.getHours() * 60 + now.getMinutes();

        // 4. Obtener rangos ocupados por OTROS grupos (para evitar que el cliente se duplique)
        const otherGroupsRanges = serviceGroups
            .filter(g => g.id !== activeGroupId && g.startTime && g.endTime)
            .map(g => ({
                start: timeToMinutes(g.startTime!),
                end: timeToMinutes(g.endTime!)
            }));

        for (const slot of allSlots) {
          const startMins = timeToMinutes(slot);
          const endMins = startMins + duration;

          // Si es hoy y la hora ya pasó, saltar
          if (isToday && startMins <= currentMinutes) {
            continue;
          }

          // Verificar disponibilidad del profesional
          const overlapsProf = taken.some((t) => {
            const tStart = timeToMinutes(t.start);
            const tEnd = timeToMinutes(t.end);
            return startMins < tEnd && tStart < endMins;
          });

          if (overlapsProf) continue;
          
          // Verificar horario comercial (usar horario dinámico según rol)
          if (endMins > timeToMinutes(scheduleClose)) continue;

          // Verificar superposición con otros servicios del cliente
          const overlapsClient = otherGroupsRanges.some(range => {
             return startMins < range.end && range.start < endMins;
          });

          if (overlapsClient) continue;

          validSlots.push(slot);
        }
        
        setAvailableSlotsForGroup(validSlots);
      } catch (err) {
        console.error('Error fetching slots', err);
        setError('Error al cargar horarios disponibles');
        setAvailableSlotsForGroup([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchSlots();
  }, [currentStep, selectedDate, activeGroupId, serviceGroups, scheduleOpen, scheduleClose, selectedBranch]); // Incluye horarios dinámicos

  const handleCategorySelect = (category: ServiceCategory) => {
    setSelectedCategory(category);
    setCurrentStep('services');
    // Scroll suave al inicio para que el usuario vea los servicios de la categoría
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const handleServiceToggle = (service: Service) => {
    setSelectedServices(prev => {
      const exists = prev.find(s => s.id === service.id);
      if (exists) {
        return prev.filter(s => s.id !== service.id);
      }
      return [...prev, service];
    });
  };

  const handleAddMoreServices = () => {
    setSelectedCategory(null);
    setCurrentStep('categories');
  };

  const handleContinueToProfessionals = () => {
    if (selectedServices.length === 0) {
      alert('Por favor seleccione al menos un servicio');
      return;
    }
    setCurrentStep('professionals');
  };

  const handleProfessionalSelect = (groupId: string, profId: number) => {
    setServiceGroups(prev => prev.map(g => 
      g.id === groupId ? { ...g, selectedProfessionalId: profId } : g
    ));
  };

  const handleContinueToSchedule = () => {
    // Validar que todos los grupos tengan profesional
    if (serviceGroups.some(g => g.selectedProfessionalId === null)) {
      alert('Por favor seleccione un profesional para cada grupo de servicios');
      return;
    }
    // Limpiar selecciones previas de horarios al entrar
    setServiceGroups(prev => prev.map(g => ({ ...g, startTime: undefined, endTime: undefined })));
    setCurrentStep('schedule');
  };

  const handleSelectSlotForGroup = (slot: string) => {
    if (!activeGroupId) return;
    
    const group = serviceGroups.find(g => g.id === activeGroupId);
    if (!group) return;

    const duration = group.services.reduce((sum, s) => sum + s.duration_minutes, 0);
    const startMins = timeToMinutes(slot);
    const endMins = startMins + duration;
    const endTime = minutesToTime(endMins);

    setServiceGroups(prev => prev.map(g => 
        g.id === activeGroupId ? { ...g, startTime: slot, endTime } : g
    ));
    setActiveGroupId(null); // Cerrar selector
  };

  const handleContinueToSummary = () => {
    // Validar que todos los grupos tengan horario
    if (serviceGroups.some(g => !g.startTime)) {
      alert('Por favor seleccione un horario para cada grupo de servicios');
      return;
    }
    setCurrentStep('summary');
  };

  const handleConfirmBooking = async () => {
    if (!customerName.trim()) {
      alert('Por favor ingrese su nombre');
      return;
    }
    try {
      setIsLoading(true);
      
      let lastPaymentUrl = null;
      let paymentErrorMsg = null;

      // Iterar sobre los grupos y crear una reserva por grupo usando sus horarios específicos
      for (const group of serviceGroups) {
        if (!group.selectedProfessionalId || !group.startTime || !group.endTime) continue;
        
        const payload = {
          professional: group.selectedProfessionalId,
          date: selectedDate.toISOString().split('T')[0],
          start_time: group.startTime,
          end_time: group.endTime,
          services: group.services.map(s => s.id),
          customer_name: customerName,
          customer_phone: customerPhone,
          customer_email: customerEmail,
        };

        let responseData;
        const token = localStorage.getItem('access_token');
        if (token) {
          const { default: api } = await import('../services/api');
          const endpoint = selectedBranch === 'santiago' ? 'appointments/bookings-santiago/' : 'appointments/bookings/';
          const res = await api.post(endpoint, payload, {
            headers: { Authorization: `Bearer ${token}` },
          });
          responseData = res.data;
        } else {
          responseData = selectedBranch === 'santiago'
            ? await appointmentService.createBookingSantiago(payload)
            : await appointmentService.createBooking(payload);
        }

        console.log("Booking Response:", responseData);

        if (responseData && responseData.payment_url) {
            lastPaymentUrl = responseData.payment_url;
        } else if (responseData && responseData.payment_error) {
            console.error("Payment error:", responseData.payment_error);
            paymentErrorMsg = responseData.payment_error;
        } else if (!responseData.payment_url) {
             console.warn("No payment URL received in response");
             paymentErrorMsg = "No se recibió la URL de pago del servidor.";
        }
      }

      if (lastPaymentUrl) {
          window.location.href = lastPaymentUrl;
          return;
      }

      if (paymentErrorMsg) {
          setError(`La reserva se creó, pero hubo un error al iniciar el pago: ${paymentErrorMsg}`);
          return;
      }

      setConfirmationMsg('¡Reserva confirmada! Te esperamos.');
      setTimeout(() => {
        setSelectedCategory(null);
        setSelectedServices([]);
        setServiceGroups([]);
        setActiveGroupId(null);
        setCustomerName('');
        setCustomerPhone('');
        setCustomerEmail('');
        setCurrentStep('categories');
        setConfirmationMsg(null);
      }, 3000);
    } catch (err: any) {
      console.error('Error creating booking', err);
      const errorDetail = err?.response?.data?.detail 
        || err?.response?.data?.message
        || err?.response?.data?.payment_error
        || err?.message
        || 'Error desconocido';
      setError(`Error al crear la reserva: ${errorDetail}. Por favor, intente de nuevo.`);
    } finally {
      setIsLoading(false);
    }
  };

  const getTotalPrice = () => selectedServices.reduce((sum, s) => sum + Number(s.price), 0);
  const getTotalDuration = () => selectedServices.reduce((sum, s) => sum + s.duration_minutes, 0);
  const getDepositAmount = () => getTotalPrice() / 2;

  // Filtrar servicios basado en búsqueda y categoría
  const filteredServices = useMemo(() => {
    if (!selectedCategory) return [];
    let services = selectedCategory.services;
    
    if (searchTerm) {
      services = services.filter(s => 
        s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    return services;
  }, [selectedCategory, searchTerm]);

  const goBack = () => {
    if (currentStep === 'categories') {
      setCurrentStep('branch');
      setSelectedBranch(null);
    } else if (currentStep === 'services') {
      setCurrentStep('categories');
      // No limpiamos selectedServices para permitir volver y añadir más
    } else if (currentStep === 'professionals') {
      setCurrentStep('services');
    } else if (currentStep === 'schedule') {
      setCurrentStep('professionals');
    } else if (currentStep === 'summary') {
      setCurrentStep('schedule');
    }
  };

  if (currentStep === 'branch') {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4 pt-[96px]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Agenda tu Cita</h1>
            <p className="text-lg text-gray-600">Selecciona la sede de tu preferencia</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
            <button
              onClick={() => { setSelectedBranch('antofagasta'); setCurrentStep('categories'); }}
              className="bg-white p-8 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-all text-center group"
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-2 group-hover:text-gray-700">Sede Antofagasta</h3>
              <p className="text-gray-500">Nuestra casa matriz</p>
            </button>
            <button
              onClick={() => { setSelectedBranch('santiago'); setCurrentStep('categories'); }}
              className="bg-white p-8 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-all text-center group"
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-2 group-hover:text-gray-700">Sede Santiago</h3>
              <p className="text-gray-500">Nueva sucursal</p>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 pt-[96px]">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Agenda tu Cita</h1>
          <p className="text-lg text-gray-600">Selecciona tus servicios y encuentra el mejor horario</p>
        </div>

        {confirmationMsg && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-8 text-center">
            <div className="flex items-center justify-center mb-2">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <p className="text-green-800 text-lg font-medium">{confirmationMsg}</p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-8 text-center">
            <p className="text-red-800 text-lg font-medium">{error}</p>
          </div>
        )}

        {/* Resumen flotante si hay servicios seleccionados en paso categorías */}
        {currentStep === 'categories' && selectedServices.length > 0 && (
           <div className="fixed bottom-6 right-6 bg-white p-4 rounded-xl shadow-2xl border-2 border-[#B4897F]/30 z-50 max-w-sm animate-fade-in-up">
             <div className="flex justify-between items-center mb-2">
               <h4 className="font-bold text-gray-900">{selectedServices.length} Servicios seleccionados</h4>
             </div>
             <p className="text-sm text-gray-600 mb-3">Total: {formatCurrency(getTotalPrice())}</p>
             <button 
               onClick={() => setCurrentStep('professionals')}
               className="w-full bg-[#B4897F] text-white py-2 text-sm font-semibold hover:bg-[#A67A6F]"
             >
               Continuar a Reserva
             </button>
           </div>
        )}

        {currentStep === 'categories' && (
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Filtro lateral */}
            <div className="lg:w-80 flex-shrink-0">
              <div className="bg-white rounded-xl shadow-md p-6 sticky top-24">
                <div className="mb-6">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="¿Qué servicio buscas?"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-accent focus:border-transparent"
                    />
                    <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                </div>
                
                <div className="space-y-0 divide-y divide-gray-200">
                  <button
                    onClick={() => setFilterCategory('Todos')}
                    className={`w-full text-left px-4 py-3 text-sm transition-colors ${
                      filterCategory === 'Todos' 
                        ? 'bg-gray-100 font-semibold text-gray-900' 
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Todos
                  </button>
                  {categories.map(cat => (
                    <button
                      key={cat.id}
                      onClick={() => setFilterCategory(cat.name)}
                      className={`w-full text-left px-4 py-3 text-sm transition-colors ${
                        filterCategory === cat.name 
                          ? 'bg-gray-100 font-semibold text-gray-900' 
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {cat.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Grid de categorías */}
            <div className="flex-1">
              <h2 className="text-2xl font-semibold text-gray-800 mb-6">Selecciona una Categoría</h2>
              {loadingCategories ? (
                <p className="text-center text-gray-600">Cargando categorías...</p>
              ) : categories.length === 0 ? (
                <p className="text-center text-gray-500">No hay categorías disponibles</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {categories
                    .filter(cat => {
                      // Filtrar por categoría seleccionada
                      if (filterCategory !== 'Todos' && cat.name !== filterCategory) return false;
                      // Filtrar por búsqueda
                      if (searchTerm) {
                        const searchLower = searchTerm.toLowerCase();
                        return cat.name.toLowerCase().includes(searchLower) ||
                               cat.services.some(s => 
                                 s.name.toLowerCase().includes(searchLower) ||
                                 s.description?.toLowerCase().includes(searchLower)
                               );
                      }
                      return true;
                    })
                    .map(cat => (
                      <div
                        key={cat.id}
                        onClick={() => handleCategorySelect(cat)}
                        className="group cursor-pointer bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl hover:scale-105 transition-all duration-300 border-2 border-gray-100 hover:border-[#B4897F]"
                      >
                        <div className="p-6">
                          <h3 className="text-xl font-bold text-gray-900 mb-2">{cat.name}</h3>
                          <p className="text-sm text-gray-600 mb-4">{cat.services.length} servicios disponibles</p>
                          <div className="flex items-center text-[#B4897F] font-medium group-hover:translate-x-2 transition-transform duration-300">
                            <span>Ver servicios</span>
                            <ChevronRight className="ml-2 w-5 h-5" />
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>
        )}

        {currentStep === 'services' && selectedCategory && (
          <div>
            <button onClick={goBack} className="flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors">
              <ArrowLeft className="w-5 h-5 mr-2" />
              Volver a categorías
            </button>
            
            {/* Título de la categoría */}
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">{selectedCategory.name}</h2>
              <p className="text-gray-600">Selecciona tus servicios y encuentra el mejor horario</p>
            </div>

            {/* Barra de búsqueda */}
            <div className="bg-white rounded-xl shadow-md p-6 mb-8">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <input
                    type="text"
                    placeholder="¿Qué servicio buscas?"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-accent focus:border-transparent"
                  />
                  <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>
            </div>

            <h3 className="text-2xl font-semibold text-gray-800 mb-4">Selecciona Servicios</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              {filteredServices.length === 0 ? (
                <div className="col-span-2 text-center py-12 text-gray-500">
                  No se encontraron servicios que coincidan con tu búsqueda
                </div>
              ) : filteredServices.map(service => {
                const isSelected = selectedServices.some(s => s.id === service.id);
                return (
                  <div
                    key={service.id}
                    onClick={() => handleServiceToggle(service)}
                    className={`cursor-pointer p-6 rounded-xl border-2 transition-all duration-200 ${
                      isSelected
                        ? 'border-[#B4897F] bg-[#B4897F]/10 shadow-lg'
                        : 'border-gray-200 bg-white hover:border-[#B4897F]/50 hover:shadow-md'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h4 className="text-lg font-bold text-gray-900 mb-2">{service.name}</h4>
                        {service.description && (
                          <p className="text-sm text-gray-600 mb-3">{service.description}</p>
                        )}
                      </div>
                      <div className={`ml-4 w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                        isSelected ? 'border-[#B4897F] bg-[#B4897F]' : 'border-gray-300'
                      }`}>
                        {isSelected && <Check className="w-4 h-4 text-white" />}
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-sm text-gray-700">
                      <div className="flex items-center">
                        <Clock className="w-4 h-4 mr-2 text-gray-500" />
                        <span>{service.duration_minutes} min</span>
                      </div>
                      <div className="flex items-center font-semibold text-[#B4897F]">
                        <span>{formatCurrency(Number(service.price))}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Botonera de acción */}
            <div className="flex flex-col sm:flex-row gap-4 justify-between items-center bg-white p-6 rounded-xl shadow-lg">
                <div className="text-gray-700">
                    <span className="font-bold">{selectedServices.length}</span> servicios seleccionados
                </div>
                <div className="flex gap-4">
                    <button
                        onClick={handleAddMoreServices}
                        className="px-6 py-3 border-2 border-[#B4897F] text-[#B4897F] font-semibold hover:bg-[#B4897F]/10 transition-colors flex items-center"
                    >
                        <Plus className="w-5 h-5 mr-2" />
                        Añadir otra categoría
                    </button>
                    <button
                        onClick={handleContinueToProfessionals}
                        disabled={selectedServices.length === 0}
                        className="bg-[#B4897F] text-white px-8 py-3 font-semibold hover:bg-[#A67A6F] transition-colors flex items-center disabled:bg-gray-300 disabled:cursor-not-allowed"
                    >
                        Continuar
                        <ChevronRight className="ml-2 w-5 h-5" />
                    </button>
                </div>
            </div>
          </div>
        )}

        {currentStep === 'professionals' && (
          <div>
             <button onClick={goBack} className="flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors">
              <ArrowLeft className="w-5 h-5 mr-2" />
              Volver a servicios
            </button>
            <h2 className="text-2xl font-semibold text-gray-800 mb-6">Selecciona Profesionales</h2>
            <p className="text-gray-600 mb-8">Hemos agrupado tus servicios para optimizar tu atención.</p>

            <div className="space-y-8">
              {serviceGroups.map((group, index) => (
                <div key={group.id} className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 border-b pb-2">
                    Grupo {index + 1}: {group.services.map(s => s.name).join(', ')}
                  </h3>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                    {group.possibleProfessionals.length > 0 ? (
                      group.possibleProfessionals.map(prof => (
                        <div
                          key={prof.id}
                          onClick={() => handleProfessionalSelect(group.id, prof.id)}
                          className={`cursor-pointer p-4 rounded-lg border-2 transition-all ${
                            group.selectedProfessionalId === prof.id
                              ? 'border-[#B4897F] bg-[#B4897F]/10'
                              : 'border-gray-200 bg-white hover:border-[#B4897F]/50'
                          }`}
                        >
                          <div className="flex items-center">
                            <User className="w-8 h-8 text-[#B4897F] mr-3" />
                            <div>
                              <p className="font-semibold text-gray-900">{prof.name || prof.full_name || `${prof.first_name} ${prof.last_name}`}</p>
                              {prof.specialty && <p className="text-xs text-gray-500">{prof.specialty}</p>}
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-red-500 col-span-3">No hay profesionales disponibles para esta combinación de servicios.</p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 flex justify-end">
               <button
                  onClick={handleContinueToSchedule}
                  className="bg-[#B4897F] text-white px-8 py-3 font-semibold hover:bg-[#A67A6F] transition-colors flex items-center"
                >
                  Ver Disponibilidad
                  <ChevronRight className="ml-2 w-5 h-5" />
                </button>
            </div>
          </div>
        )}

        {currentStep === 'schedule' && (
          <div>
            <button onClick={goBack} className="flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors">
              <ArrowLeft className="w-5 h-5 mr-2" />
              Volver a profesionales
            </button>
            <h2 className="text-2xl font-semibold text-gray-800 mb-6">Selecciona Fecha y Horarios</h2>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
              <div className="bg-white rounded-xl shadow-lg p-6 h-fit">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Calendar className="w-5 h-5 mr-2 text-[#B4897F]" />
                  Selecciona la fecha
                </h3>
                <input
                  type="date"
                  value={selectedDate.toISOString().split('T')[0]}
                  onChange={e => {
                      const newDate = new Date(e.target.value + 'T00:00:00');
                      // Validar que no sea domingo (0 = domingo)
                      if (newDate.getDay() === 0) {
                          setError('Los domingos no están disponibles para reservas online. Por favor selecciona otro día.');
                          // Resetear a la fecha actual o mantener la fecha anterior
                          return;
                      }
                      setError(null);
                      setSelectedDate(newDate);
                      // Limpiar horarios al cambiar fecha
                      setServiceGroups(prev => prev.map(g => ({ ...g, startTime: undefined, endTime: undefined })));
                  }}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                <p className="text-sm text-gray-500 mt-2">
                  * Disponible de Lunes a Sábado
                </p>
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Resumen de servicios</h4>
                  <div className="space-y-2">
                    {selectedServices.map(service => (
                      <div key={service.id} className="flex justify-between text-sm">
                        <span className="text-gray-600">{service.name}</span>
                        <span className="text-gray-900 font-medium">{service.duration_minutes} min</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 pt-3 border-t border-gray-200 flex justify-between font-semibold">
                    <span className="text-gray-900">Duración total</span>
                    <span className="text-[#B4897F]">{getTotalDuration()} min</span>
                  </div>
                </div>
              </div>
              
              <div className="lg:col-span-2 space-y-6">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <Clock className="w-5 h-5 mr-2 text-[#B4897F]" />
                  Configura el horario para cada grupo
                </h3>
                
                {serviceGroups.map((group, index) => {
                    const prof = allProfessionals.find(p => p.id === group.selectedProfessionalId);
                    const isActive = activeGroupId === group.id;
                    const isCompleted = !!group.startTime;

                    return (
                        <div key={group.id} className={`bg-white rounded-xl shadow-lg overflow-hidden border-2 transition-all ${isActive ? 'border-[#B4897F] ring-2 ring-[#B4897F]/20' : isCompleted ? 'border-green-500' : 'border-gray-100'}`}>
                            <div className="p-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                                <div>
                                    <h4 className="font-bold text-gray-900 text-lg">
                                        {prof ? (prof.name || prof.full_name || `${prof.first_name} ${prof.last_name}`) : 'Profesional'}
                                    </h4>
                                    <p className="text-sm text-gray-600 mt-1">
                                        Servicios: {group.services.map(s => s.name).join(', ')}
                                    </p>
                                    <p className="text-xs text-gray-500 mt-1">
                                        Duración: {group.services.reduce((sum, s) => sum + s.duration_minutes, 0)} min
                                    </p>
                                </div>
                                
                                <div className="flex items-center gap-3">
                                    {isCompleted ? (
                                        <div className="flex items-center bg-green-50 text-green-700 px-4 py-2 rounded-lg border border-green-200">
                                            <Clock className="w-4 h-4 mr-2" />
                                            <span className="font-bold">{group.startTime} - {group.endTime}</span>
                                            <button 
                                                onClick={() => setActiveGroupId(group.id)}
                                                className="ml-3 p-1 hover:bg-green-100 rounded-full transition-colors"
                                                title="Cambiar horario"
                                            >
                                                <Edit2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ) : (
                                        <button
                                            onClick={() => setActiveGroupId(isActive ? null : group.id)}
                                            className={`px-6 py-2 font-semibold transition-colors ${isActive ? 'bg-gray-200 text-gray-700' : 'bg-[#B4897F] text-white hover:bg-[#A67A6F]'}`}
                                        >
                                            {isActive ? 'Cancelar' : 'Seleccionar Hora'}
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Selector de horarios expandible */}
                            {isActive && (
                                <div className="border-t border-gray-100 bg-gray-50 p-6 animate-fade-in">
                                    <div className="flex justify-between items-center mb-4">
                                        <p className="text-sm font-medium text-gray-700">Horarios disponibles para {prof?.name || 'este profesional'}:</p>
                                        <button onClick={() => setActiveGroupId(null)} className="text-gray-400 hover:text-gray-600">
                                            <X className="w-5 h-5" />
                                        </button>
                                    </div>
                                    
                                    {isLoading ? (
                                        <p className="text-center text-gray-600 py-4">Cargando horarios...</p>
                                    ) : availableSlotsForGroup.length === 0 ? (
                                        <p className="text-center text-red-500 py-4">
                                            No hay horarios disponibles que no se superpongan con tus otras selecciones.
                                        </p>
                                    ) : (
                                        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3 max-h-60 overflow-y-auto">
                                            {availableSlotsForGroup.map(slot => (
                                                <button
                                                    key={slot}
                                                    onClick={() => handleSelectSlotForGroup(slot)}
                                                    className="py-2 px-3 bg-white border border-gray-200 text-sm font-medium text-gray-700 hover:border-[#B4897F] hover:text-[#B4897F] hover:shadow-md transition-all"
                                                >
                                                    {slot}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}

                <div className="flex justify-end mt-8 pt-6 border-t border-gray-200">
                    <button
                      onClick={handleContinueToSummary}
                      disabled={serviceGroups.some(g => !g.startTime)}
                      className="bg-[#B4897F] text-white px-8 py-3 font-semibold hover:bg-[#A67A6F] transition-colors flex items-center disabled:bg-gray-300 disabled:cursor-not-allowed"
                    >
                      Continuar al Resumen
                      <ChevronRight className="ml-2 w-5 h-5" />
                    </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {currentStep === 'summary' && (
          <div>
            <button onClick={goBack} className="flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors">
              <ArrowLeft className="w-5 h-5 mr-2" />
              Volver a horarios
            </button>
            <h2 className="text-2xl font-semibold text-gray-800 mb-6">Resumen de Reserva</h2>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                <div className="bg-white rounded-xl shadow-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Detalles de la Cita</h3>
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <Calendar className="w-5 h-5 text-[#B4897F] mr-3 mt-1" />
                      <div>
                        <p className="text-sm text-gray-600">Fecha</p>
                        <p className="font-medium text-gray-900">
                          {selectedDate.toLocaleDateString('es-CL', { 
                            weekday: 'long', 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          })}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <Clock className="w-5 h-5 text-[#B4897F] mr-3 mt-1" />
                      <div>
                        <p className="text-sm text-gray-600">Horarios</p>
                        <div className="font-medium text-gray-900">
                            {serviceGroups.map((g, i) => (
                                <div key={i} className="text-sm">
                                    {g.startTime} - {g.endTime} 
                                    <span className="text-gray-500 text-xs ml-2">
                                        ({allProfessionals.find(p => p.id === g.selectedProfessionalId)?.name || 'Prof.'})
                                    </span>
                                </div>
                            ))}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <User className="w-5 h-5 text-[#B4897F] mr-3 mt-1" />
                      <div>
                        <p className="text-sm text-gray-600">Profesionales</p>
                        <div className="font-medium text-gray-900">
                          {serviceGroups.map((g, i) => {
                             const prof = allProfessionals.find(p => p.id === g.selectedProfessionalId);
                             return (
                               <div key={i}>
                                 {prof ? (prof.name || prof.full_name || `${prof.first_name} ${prof.last_name}`) : 'N/A'} 
                                 <span className="text-gray-500 text-xs ml-2">({g.services.map(s => s.name).join(', ')})</span>
                               </div>
                             );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl shadow-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Servicios Seleccionados</h3>
                  <div className="space-y-3">
                    {selectedServices.map(service => (
                      <div key={service.id} className="flex justify-between items-center py-3 border-b border-gray-100 last:border-0">
                        <div>
                          <p className="font-medium text-gray-900">{service.name}</p>
                          <p className="text-sm text-gray-600">{service.duration_minutes} minutos</p>
                        </div>
                        <p className="font-semibold text-gray-900">{formatCurrency(Number(service.price))}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 pt-4 border-t-2 border-gray-200 flex justify-between items-center">
                    <p className="text-lg font-bold text-gray-900">Total</p>
                    <p className="text-2xl font-bold text-[#B4897F]">{formatCurrency(getTotalPrice())}</p>
                  </div>
                </div>
                <div className="bg-white rounded-xl shadow-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Tus Datos</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Nombre Completo *</label>
                      <input
                        type="text"
                        value={customerName}
                        onChange={e => setCustomerName(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#B4897F] focus:border-transparent"
                        placeholder="Tu nombre"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Teléfono</label>
                      <input
                        type="tel"
                        value={customerPhone}
                        onChange={e => {
                          const value = e.target.value;
                          // Solo permitir números, espacios y el símbolo +
                          if (/^[0-9+\s]*$/.test(value)) {
                            setCustomerPhone(value);
                          }
                        }}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#B4897F] focus:border-transparent"
                        placeholder="+56 9 xxxx xxxx"
                        pattern="[0-9+\s]*"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                      <input
                        type="email"
                        value={customerEmail}
                        onChange={e => setCustomerEmail(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#B4897F] focus:border-transparent"
                        placeholder="tu@email.com"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="lg:col-span-1">
                <div className="bg-gradient-to-br from-[#B4897F]/10 to-[#B4897F]/5 rounded-xl shadow-lg p-6 sticky top-6">
                  <div className="bg-white rounded-lg p-6 mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Abono del 50%</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Para confirmar tu reserva, debes abonar el 50% del valor total de los servicios.
                    </p>
                    <div className="bg-[#B4897F]/10 rounded-lg p-4 border-2 border-[#B4897F]/30">
                      <p className="text-sm text-gray-600 mb-1">Monto a pagar ahora</p>
                      <p className="text-3xl font-bold text-[#B4897F]">{formatCurrency(getDepositAmount())}</p>
                    </div>
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-gray-600">Total servicios:</span>
                        <span className="font-medium text-gray-900">{formatCurrency(getTotalPrice())}</span>
                      </div>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-gray-600">Abono (50%):</span>
                        <span className="font-medium text-gray-900">{formatCurrency(getDepositAmount())}</span>
                      </div>
                      <div className="flex justify-between text-sm font-semibold text-[#B4897F] pt-2 border-t border-gray-200">
                        <span>Pago en tienda:</span>
                        <span>{formatCurrency(getDepositAmount())}</span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={handleConfirmBooking}
                    disabled={isLoading || !customerName.trim()}
                    className="w-full bg-[#B4897F] text-white py-4 font-semibold hover:bg-[#A67A6F] transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {isLoading ? (
                      'Confirmando...'
                    ) : (
                      <>
                        <Check className="w-5 h-5 mr-2" />
                        Confirmar Reserva
                      </>
                    )}
                  </button>
                  <p className="text-xs text-gray-500 text-center mt-4">
                    El saldo restante se paga en la tienda al momento de la atención
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SchedulingPage;
